import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ScrollText, Loader2 } from 'lucide-react';
import { logsApi, userApi } from '../services/api';
import {
    AuditAction,
    AUDIT_ACTION_LABELS,
    AuditLog,
    User,
} from '../types';
import Alert from '../components/Alert';
import DataTable from '../components/DataTable';
import Modal from '../components/Modal';
import { getCurrentMonthDateRange } from '../utils/dateUtils';
import { hasAuditMetadata, normalizeAuditMetadata } from '../utils/auditLogUtils';

const itemsPerPage = 10;

const defaultMonthRange = getCurrentMonthDateRange();

const auditActionOptions = Object.values(AuditAction)
    .map((value) => ({ value, label: AUDIT_ACTION_LABELS[value] }))
    .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'));

function resolveDateRange(startDate: string, endDate: string): { startDate: string; endDate: string } {
    const fallback = getCurrentMonthDateRange();
    return {
        startDate: startDate || fallback.startDate,
        endDate: endDate || fallback.endDate,
    };
}

const AdminAuditLogs: React.FC = () => {
    const [filterUserId, setFilterUserId] = useState('');
    const [filterAction, setFilterAction] = useState<AuditAction | ''>('');
    const [filterStartDate, setFilterStartDate] = useState(defaultMonthRange.startDate);
    const [filterEndDate, setFilterEndDate] = useState(defaultMonthRange.endDate);

    const [users, setUsers] = useState<User[]>([]);
    const [logs, setLogs] = useState<AuditLog[]>([]);
    const [filteredLogs, setFilteredLogs] = useState<AuditLog[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [metadataModalLog, setMetadataModalLog] = useState<AuditLog | null>(null);

    const userMap = useMemo(() => {
        const map: Record<string, User> = {};
        users.forEach((user) => {
            map[user.id] = user;
        });
        return map;
    }, [users]);

    const loadUsers = useCallback(async () => {
        try {
            const response = await userApi.getAll();
            setUsers(response.data || []);
        } catch {
            setUsers([]);
        }
    }, []);

    const fetchLogs = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const { startDate, endDate } = resolveDateRange(filterStartDate, filterEndDate);
            const params: {
                userId?: string;
                action?: AuditAction;
                startDate: string;
                endDate: string;
            } = { startDate, endDate };

            if (filterUserId) params.userId = filterUserId;
            if (filterAction) params.action = filterAction;

            const response = await logsApi.getAll(params);
            const data = (response.data || []).slice().sort(
                (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
            );
            setLogs(data);
        } catch (e: unknown) {
            const friendly =
                (e as { response?: { data?: { translatedMessage?: string } } })?.response?.data
                    ?.translatedMessage || 'Não foi possível carregar os logs.';
            setError(friendly);
            setLogs([]);
        } finally {
            setLoading(false);
        }
    }, [filterUserId, filterAction, filterStartDate, filterEndDate]);

    useEffect(() => {
        loadUsers();
    }, [loadUsers]);

    useEffect(() => {
        fetchLogs();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const term = searchTerm.toLowerCase();
        const filtered = logs.filter((log) => {
            const user = userMap[log.userId];
            const userLabel = user ? `${user.name} ${user.username}` : log.userId;
            const actionLabel = AUDIT_ACTION_LABELS[log.action] || log.action;
            return (
                log.description.toLowerCase().includes(term) ||
                log.action.toLowerCase().includes(term) ||
                actionLabel.toLowerCase().includes(term) ||
                userLabel.toLowerCase().includes(term)
            );
        });
        setFilteredLogs(filtered);
        setCurrentPage(1);
    }, [searchTerm, logs, userMap]);

    const totalPages = Math.max(1, Math.ceil(filteredLogs.length / itemsPerPage));
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedData = filteredLogs.slice(startIndex, startIndex + itemsPerPage);

    const metadataChanges = metadataModalLog
        ? normalizeAuditMetadata(metadataModalLog.metadata)
        : [];

    const columns = [
        {
            key: 'createdAt',
            label: 'Data',
            render: (value: string) => (
                <span className="whitespace-nowrap">{new Date(value).toLocaleString('pt-BR')}</span>
            ),
        },
        {
            key: 'userId',
            label: 'Usuário',
            render: (_value: string, log: AuditLog) => {
                const user = userMap[log.userId];
                if (!user) {
                    return <span className="font-mono text-xs text-secondary-600">{log.userId}</span>;
                }
                return (
                    <div>
                        <div className="font-medium">{user.name}</div>
                        <div className="text-xs text-secondary-600">@{user.username}</div>
                    </div>
                );
            },
        },
        {
            key: 'action',
            label: 'Ação',
            render: (value: AuditAction) => (
                <span className="inline-flex rounded-full bg-primary-50 px-2.5 py-1 text-xs font-medium text-primary-700">
                    {AUDIT_ACTION_LABELS[value] || value}
                </span>
            ),
        },
        {
            key: 'description',
            label: 'Descrição',
        },
        {
            key: 'metadata',
            label: 'Detalhes',
            render: (_value: unknown, log: AuditLog) => {
                if (!hasAuditMetadata(log.metadata)) {
                    return <span className="text-secondary-400">—</span>;
                }
                return (
                    <button
                        type="button"
                        onClick={() => setMetadataModalLog(log)}
                        className="text-sm text-primary-600 hover:text-primary-800 font-medium"
                    >
                        Ver detalhes
                    </button>
                );
            },
        },
    ];

    return (
        <div className="space-y-6">
            <div className="flex items-center gap-3">
                <div className="p-3 bg-primary-100 rounded-lg">
                    <ScrollText className="h-6 w-6 text-primary-600" />
                </div>
                <div>
                    <h1 className="text-2xl font-bold text-secondary-900">Logs de auditoria</h1>
                    <p className="text-secondary-600">Histórico de ações realizadas no sistema</p>
                </div>
            </div>

            {error && <Alert type="error" title="Erro ao carregar" message={error} />}

            <div className="bg-white p-4 rounded-lg border border-secondary-200">
                <div className="flex flex-wrap items-end gap-3">
                    <div className="w-full sm:w-auto sm:min-w-[200px]">
                        <label className="block text-sm font-medium text-secondary-700 mb-1">Usuário</label>
                        <select
                            className="input-field"
                            value={filterUserId}
                            onChange={(e) => setFilterUserId(e.target.value)}
                        >
                            <option value="">Todos</option>
                            {users.map((user) => (
                                <option key={user.id} value={user.id}>
                                    {user.name} (@{user.username})
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="w-full sm:w-auto sm:min-w-[220px]">
                        <label className="block text-sm font-medium text-secondary-700 mb-1">Ação</label>
                        <select
                            className="input-field"
                            value={filterAction}
                            onChange={(e) => setFilterAction((e.target.value || '') as AuditAction | '')}
                        >
                            <option value="">Todas</option>
                            {auditActionOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="w-full sm:w-auto">
                        <label className="block text-sm font-medium text-secondary-700 mb-1">Data inicial</label>
                        <input
                            type="date"
                            className="input-field"
                            value={filterStartDate}
                            onChange={(e) => setFilterStartDate(e.target.value)}
                        />
                    </div>
                    <div className="w-full sm:w-auto">
                        <label className="block text-sm font-medium text-secondary-700 mb-1">Data final</label>
                        <input
                            type="date"
                            className="input-field"
                            value={filterEndDate}
                            onChange={(e) => setFilterEndDate(e.target.value)}
                        />
                    </div>
                    <button
                        type="button"
                        onClick={fetchLogs}
                        className="btn-primary w-full sm:w-auto shrink-0 h-[42px]"
                    >
                        Filtrar
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 text-primary-600 animate-spin" />
                </div>
            ) : logs.length === 0 ? (
                <div className="bg-white p-8 rounded-lg border border-secondary-200 text-center">
                    <ScrollText className="w-12 h-12 text-secondary-400 mx-auto mb-4" />
                    <p className="text-secondary-600">Nenhum log encontrado para os filtros selecionados.</p>
                </div>
            ) : (
                <div className="bg-white rounded-lg border border-secondary-200">
                    <DataTable
                        data={paginatedData}
                        columns={columns}
                        searchPlaceholder="Buscar por descrição, ação ou usuário..."
                        searchValue={searchTerm}
                        onSearchChange={setSearchTerm}
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                    />
                </div>
            )}

            <Modal
                isOpen={Boolean(metadataModalLog)}
                onClose={() => setMetadataModalLog(null)}
                title="Detalhes do log"
            >
                {metadataModalLog && (
                    <div className="space-y-4">
                        <p className="text-sm text-secondary-600">{metadataModalLog.description}</p>
                        {metadataChanges.length > 0 ? (
                            <div className="overflow-x-auto rounded-lg border border-secondary-200">
                                <table className="min-w-full divide-y divide-secondary-200 text-sm">
                                    <thead className="bg-secondary-50">
                                        <tr>
                                            <th className="table-header">Campo</th>
                                            <th className="table-header">Valor anterior</th>
                                            <th className="table-header">Novo valor</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-secondary-200">
                                        {metadataChanges.map((change, index) => (
                                            <tr key={`${change.fieldName}-${index}`}>
                                                <td className="table-cell font-medium">{change.fieldName}</td>
                                                <td className="table-cell text-secondary-600 whitespace-pre-wrap">
                                                    {change.oldValue || '—'}
                                                </td>
                                                <td className="table-cell whitespace-pre-wrap">
                                                    {change.newValue || '—'}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <p className="text-sm text-secondary-500">Nenhum detalhe disponível.</p>
                        )}
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default AdminAuditLogs;
